import { Node, mergeAttributes } from "@tiptap/core";

/**
 * Extract a YouTube video ID from various URL formats.
 */
export function extractYouTubeId(url) {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/,
  );
  return match?.[1] || null;
}

/**
 * Custom TipTap node for YouTube video embeds in newsletters.
 *
 * In the editor: NodeView shows thumbnail + play button + delete button.
 * In email HTML: renders as a linked thumbnail image — email-safe, no JS needed.
 */
export const YouTubeNode = Node.create({
  name: "youtube",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      videoId: { default: null },
      src: { default: null }, // full YouTube URL
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-youtube]",
        getAttrs: (dom) => {
          const videoId = dom.getAttribute("data-youtube");
          const a = dom.querySelector("a");
          return {
            videoId,
            src: a?.getAttribute("href") || `https://www.youtube.com/watch?v=${videoId}`,
          };
        },
      },
    ];
  },

  // renderHTML is used for email output (editor.getHTML()) — must be email-safe
  renderHTML({ node, HTMLAttributes }) {
    const { videoId, src } = node.attrs;
    const thumb = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    const url = src || `https://www.youtube.com/watch?v=${videoId}`;

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-youtube": videoId,
        style: "text-align:center;margin:16px 0;",
      }),
      [
        "a",
        {
          href: url,
          target: "_blank",
          style: "display:inline-block;text-decoration:none;",
        },
        [
          "img",
          {
            src: thumb,
            alt: "Watch video",
            style:
              "max-width:100%;height:auto;display:block;border-radius:8px;",
          },
        ],
      ],
    ];
  },

  // NodeView for the editor — shows thumbnail + play button + delete button
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const { videoId, src } = node.attrs;
      const thumb = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      const url = src || `https://www.youtube.com/watch?v=${videoId}`;

      // Container
      const dom = document.createElement("div");
      dom.setAttribute("data-youtube", videoId);
      dom.style.cssText = "text-align:center;margin:16px 0;";

      // Wrapper for relative positioning
      const wrapper = document.createElement("div");
      wrapper.style.cssText = "display:inline-block;position:relative;";

      // Thumbnail link
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.style.cssText = "display:block;text-decoration:none;";
      link.addEventListener("click", (e) => e.preventDefault());

      const img = document.createElement("img");
      img.src = thumb;
      img.alt = "Watch video";
      img.style.cssText = "max-width:100%;height:auto;display:block;border-radius:8px;";

      link.appendChild(img);

      // Play button overlay (CSS handles the triangle via ::before/::after on the link)
      const play = document.createElement("div");
      play.style.cssText =
        "position:absolute;top:12px;right:12px;" +
        "width:68px;height:48px;background:rgba(255,0,0,0.85);border-radius:14px;" +
        "box-shadow:0 2px 8px rgba(0,0,0,0.3);pointer-events:none;" +
        "display:flex;align-items:center;justify-content:center;";
      // Play triangle
      const triangle = document.createElement("div");
      triangle.style.cssText =
        "width:0;height:0;border-style:solid;border-width:10px 0 10px 18px;" +
        "border-color:transparent transparent transparent white;margin-left:3px;";
      play.appendChild(triangle);

      // Delete button
      const deleteBtn = document.createElement("button");
      deleteBtn.innerHTML = "&times;";
      deleteBtn.title = "Remove video";
      deleteBtn.style.cssText =
        "position:absolute;top:8px;right:8px;width:28px;height:28px;" +
        "border-radius:50%;border:none;background:rgba(0,0,0,0.6);" +
        "color:white;font-size:18px;line-height:1;cursor:pointer;" +
        "display:flex;align-items:center;justify-content:center;" +
        "opacity:0;transition:opacity 0.15s;z-index:2;";
      deleteBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const pos = getPos();
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
      });

      // Show delete on hover
      wrapper.addEventListener("mouseenter", () => { deleteBtn.style.opacity = "1"; });
      wrapper.addEventListener("mouseleave", () => { deleteBtn.style.opacity = "0"; });

      wrapper.appendChild(link);
      wrapper.appendChild(play);
      wrapper.appendChild(deleteBtn);
      dom.appendChild(wrapper);

      return { dom };
    };
  },

  addCommands() {
    return {
      setYouTube:
        (attrs) =>
        ({ chain }) => {
          return chain()
            .insertContent({ type: this.name, attrs })
            .run();
        },
    };
  },
});
